import { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import { Button, Switch, Modal } from '@/components/ui';
import { SystemBannerSlot } from '@/components/domain/shared/SystemBannerSlot';
import {
  useAuth,
  useGmailConnectionStatus,
  useAppShell,
  useUserRole,
} from '@/hooks';
import type { GmailConnectionStatus } from '@/hooks';
import { toast, useGmailConnectionStore } from '@/stores';
import { householdService } from '@/services';
import { SettingsForwardingSkeleton } from './skeletons';
import {
  setupGmailForwarding,
  ForwardingSetupError,
  UnsupportedAccountError,
} from '@/services/gmail-forwarding.service';
import type { ForwardingSetupErrorCode } from '@/services/gmail-forwarding.service';
import { REQUIRED_GMAIL_SCOPES } from '@/services/auth.service';
import { UnsupportedAccountNotice } from '@/pages/onboarding/UnsupportedAccountNotice';
import { supabase } from '@/lib/supabase';

type ConnectionState =
  | 'not_connected'
  | 'connected'
  | 'paused'
  | 'reconnect'
  | 'internal_issue';

// Set right before we hand off to Google so that when the user lands back on
// /settings/forwarding with a fresh `provider_token`, we know this OAuth round
// trip was started by *this* page and can finish setup automatically. Without
// this, an unrelated session that happens to carry a `provider_token` could
// trigger setup on mount.
const OAUTH_IN_PROGRESS_KEY = 'yaruta:gmail_oauth_in_progress';

const CONNECT_FAILED_TOAST = 'Failed to connect Gmail';

// YAR-332: coded refusals from `gmail-forwarding-setup`. Each says what went
// wrong instead of the generic toast, which gave no hint at the fix.
const SETUP_ERROR_TOAST: Record<
  Exclude<ForwardingSetupErrorCode, 'UNKNOWN'>,
  string
> = {
  NOT_HOUSEHOLD_PRIMARY: 'Only your household admin can connect Gmail.',
  NOT_HOUSEHOLD_MEMBER: 'You’re not a member of this household.',
  GMAIL_PROFILE_UNAVAILABLE:
    'Gmail didn’t confirm your account. Reconnect to try again.',
};

function connectFailureToast(err: unknown): string {
  if (err instanceof ForwardingSetupError && err.code !== 'UNKNOWN') {
    return SETUP_ERROR_TOAST[err.code];
  }
  return CONNECT_FAILED_TOAST;
}

// Refusals a fresh provider_token cannot change. Unlike a Gmail profile
// failure (which a stale token does cause), these must not be absorbed as the
// stale-session race.
function isDeterministicSetupRefusal(err: unknown): boolean {
  return (
    err instanceof ForwardingSetupError &&
    (err.code === 'NOT_HOUSEHOLD_PRIMARY' ||
      err.code === 'NOT_HOUSEHOLD_MEMBER')
  );
}

function mapGmailStatusToState(
  status: GmailConnectionStatus | undefined,
): ConnectionState {
  if (!status) return 'not_connected';
  if (status.state === 'connected') return 'connected';
  if (status.state === 'paused') return 'paused';
  if (
    status.state === 'expired' ||
    status.state === 'expiring_soon' ||
    // Recoverable renewal failure — the Reconnect CTA's soft refresh
    // re-arms the watch and clears the flag without a Google re-consent.
    status.state === 'needs_attention' ||
    // Google refused the stored grant (the parent revoked Yaruta, or it
    // aged out). It used to fall through to "Not connected", which hid the
    // Reconnect CTA AC9 of YAR-150 asks for. Reconnect's soft refresh gets
    // needs_oauth back and continues into the OAuth round trip.
    status.state === 'revoked'
  ) {
    return 'reconnect';
  }
  return 'not_connected';
}

// Figma draws the status chip as the DS Badge (component set 52:32) at its
// `sm` size: a pill whose fill, ring, dot and label all come from ONE state
// of that component. Each screen state is sampled from its own frame:
//   connected        — 7761:48697 (State=success): success-light / success
//   not connected    — 7733:37728 (State=error):   danger-light (#fff2f2) /
//                      border-error, dot + label #a82222
//   paused           — 7761:48987 (State=outline): surface (#ffffff) /
//                      border (#e5e5e5), dot + label disabled #999999
//   connection issue — 7761:48593 and 7761:48630 (State=warning):
//                      warning-light (#fff7f2) / warning (#a24613)
// YAR-150 QA (Oct 16): Paused had been given the error tint and Connection
// issue had no fill and a neutral ring; both now follow their Figma state.
// `danger-border` (#fde6e6) is NOT the error ring — it is the hairline of a
// pale error surface; the badge outline is `border-error` (#a82222).
const badgeConfig: Record<
  ConnectionState,
  {
    label: string;
    dotColor: string;
    textColor: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  not_connected: {
    label: 'Not connected',
    dotColor: 'bg-icon-error',
    textColor: 'text-text-error',
    bgColor: 'bg-danger-light',
    borderColor: 'border-border-error',
  },
  connected: {
    label: 'Connected',
    dotColor: 'bg-success',
    textColor: 'text-success',
    bgColor: 'bg-success-light',
    borderColor: 'border-success',
  },
  paused: {
    label: 'Paused',
    dotColor: 'bg-icon-disabled',
    textColor: 'text-disabled-text',
    bgColor: 'bg-surface',
    borderColor: 'border-border',
  },
  reconnect: {
    label: 'Connection issue',
    dotColor: 'bg-warning',
    textColor: 'text-warning',
    bgColor: 'bg-warning-light',
    borderColor: 'border-warning',
  },
  internal_issue: {
    label: 'Connection issue',
    dotColor: 'bg-warning',
    textColor: 'text-warning',
    bgColor: 'bg-warning-light',
    borderColor: 'border-warning',
  },
};

// The screen's secondary CTA. Figma gives both footer buttons the same 44px
// height (YAR-230), which neither the shared Button's `md` (37.6px) nor its
// `lg` (46px) padding scale lands on, so the height is pinned with `h-11`
// and the line box is centred inside it.
function DisconnectButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-11 flex items-center justify-center border border-danger text-danger font-semibold text-lg leading-7 px-2.5 rounded-md cursor-pointer"
    >
      Disconnect
    </button>
  );
}

export function SettingsForwarding() {
  const { session } = useAuth();
  const { householdId } = useAppShell();
  const { isPrimary, roleResolved } = useUserRole();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  // True while a pause / resume request is in flight. The switch is locked
  // until it settles, so rapid taps send one request and show one toast
  // (YAR-150) instead of one of each per tap.
  const [toggling, setToggling] = useState(false);
  // Gate 3: when the connected mailbox is unsupported (Workspace/business
  // domain, or missing scopes), setupGmailForwarding throws
  // UnsupportedAccountError. We render an inline notice instead of the generic
  // failure toast / stale-session retry.
  const [unsupported, setUnsupported] = useState<{
    reason: 'non_personal_domain' | 'missing_scopes';
    email?: string;
  } | null>(null);

  const { data: gmailStatus, refetch: refetchGmailStatus } =
    useGmailConnectionStatus(householdId ?? null);

  const state: ConnectionState = mapGmailStatusToState(gmailStatus);

  useEffect(() => {
    setLoading(false);
  }, [householdId]);

  // Ask Google, once per visit, whether the stored grant still works. The
  // plain status read only knows about a revocation once a Gmail push or the
  // renewal job has tripped over it, so a parent who removed Yaruta in their
  // Google Account kept seeing "Connected" here (YAR-150).
  useEffect(() => {
    if (!householdId || !isPrimary) return;
    let cancelled = false;
    householdService
      .getGmailSubscriptionStatus(householdId, { verify: true })
      .then((sub) => {
        if (!cancelled && sub?.revoked_at) refetchGmailStatus();
      })
      .catch(() => {
        // Best effort: the ordinary status read already rendered the screen.
      });
    return () => {
      cancelled = true;
    };
    // refetchGmailStatus is a fresh closure every render; once per visit is
    // the point, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId, isPrimary]);

  // Auto-resume after returning from Google consent. signInWithOAuth does a
  // full-page redirect, so the click handler that triggered it never sees the
  // resulting token. Onboarding's StepAutoForwarding handles this by polling
  // for `provider_token` on a dedicated screen; Settings needs the same
  // treatment, otherwise the user lands back here with the OAuth done, the
  // page showing "Not connected", and no obvious next step.
  //
  // The sessionStorage flag is the primary gate: we set it right before
  // signInWithOAuth and clear it here. We deliberately do NOT require
  // `state === 'not_connected'` because Reconnect can be triggered from
  // `paused` or `expired` too — in those cases the DB still shows the old
  // state during the callback, and gating on `state === 'not_connected'`
  // would force the user to click Reconnect a second time. We only short-
  // circuit when the DB already says `connected`, since setup has nothing
  // to do then.
  //
  // We deliberately do NOT use a per-effect `cancelled` flag the way a
  // typical async useEffect does. `useGmailConnectionStatus` returns a
  // brand-new `refetch` closure on every render, which forces this effect
  // to re-run frequently while setup is in flight. The classic cancel
  // pattern would mark `cancelled=true` on every such re-run and discard
  // the setup result. Instead, `dispatchedRef` makes sure we fire setup at
  // most once per mount, and `mountedRef` only gates the history-rewrite
  // side-effect (toast and refetch are safe globals).
  //
  // The remaining quirk we have to handle is the "stale session race":
  // when the user returns from Google with `?code=...`, Supabase reads the
  // cached session out of localStorage first (with the OLD provider_token,
  // which the Disconnect just revoked at Google) and only afterwards
  // exchanges the code and emits SIGNED_IN with the FRESH tokens. The
  // effect can therefore fire once with the revoked token, get a 403 from
  // gmail.users.getProfile, and burn the OAUTH_IN_PROGRESS_KEY flag — when
  // the fresh provider_token finally arrives the effect re-runs but has
  // nothing to do, and the user is stuck looking at "Reconnect Gmail"
  // until they click through the OAuth flow a second time.
  //
  // Mitigation:
  //  - `lastTriedTokenRef` remembers which provider_token we already
  //    attempted. If the same value comes back through deps we skip,
  //    waiting for a different one.
  //  - The flag is only cleared on SUCCESS, so the next provider_token
  //    update still triggers a retry.
  //  - `hasShownErrorToastRef` suppresses the toast on the first failure
  //    (the expected race outcome). If a *second*, different token also
  //    fails, that's a real problem and we surface it + clear the flag.
  const dispatchedRef = useRef(false);
  const mountedRef = useRef(true);
  const lastTriedTokenRef = useRef<string | null>(null);
  const hasShownErrorToastRef = useRef(false);
  // Bump to force the dispatch effect to re-evaluate from inside .catch when
  // a newer provider_token arrived from Supabase *while* the previous
  // setupGmailForwarding HTTP call was in flight. Without this nudge the
  // effect's re-run for the new token was already blocked by the in-flight
  // dispatchedRef=true and would never fire again on its own.
  const [retryNonce, setRetryNonce] = useState(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (dispatchedRef.current) return;
    if (!householdId) return;
    if (gmailStatus?.state === 'connected') return;
    const token = session?.provider_token;
    if (!token) return;
    if (token === lastTriedTokenRef.current) return;

    let inProgress: string | null = null;
    try {
      inProgress = sessionStorage.getItem(OAUTH_IN_PROGRESS_KEY);
    } catch {
      // Storage unavailable — fall back to manual click flow.
      return;
    }
    if (inProgress !== '1') return;

    dispatchedRef.current = true;
    lastTriedTokenRef.current = token;
    setConnecting(true);
    setupGmailForwarding(householdId, token, session?.provider_refresh_token)
      .then((result) => {
        // setupGmailForwarding returns HTTP 200 even when Stage A (label
        // creation) fails — Stage B/C are skipped, no subscription is
        // persisted, but the response shape is the same. Treat a missing
        // label_id as a thrown error so the stale-session retry logic
        // below kicks in (the most common cause is a stale provider_token
        // that hits Gmail with insufficient scopes). Onboarding's
        // StepAutoForwarding has the equivalent check.
        if (!result.label_id) {
          console.error(
            '[SettingsForwarding] Gmail setup returned no label_id:',
            result.errors,
          );
          throw new Error('Gmail setup did not create the Yaruta label');
        }
        // Success — consume the flag and apply the post-setup side-effects.
        try {
          sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
        } catch {
          // Ignore — clearing is best-effort.
        }
        refetchGmailStatus();
        toast.success('Gmail connected');
        // Rewrite history so the user's first Back goes to /settings
        // instead of back to Google's consent screen. The OAuth redirect
        // leaves history as [...prev, google-consent, /settings/forwarding?code=...]
        // — replacing the current entry with /settings and pushing a clean
        // /settings/forwarding on top yields:
        //   [...prev, google-consent, /settings, /settings/forwarding (current)]
        // Only rewrite if we're still mounted on this page; otherwise the
        // user navigated away and we shouldn't touch their location.
        if (!mountedRef.current) return;
        try {
          window.history.replaceState(null, '', '/settings');
          window.history.pushState(null, '', '/settings/forwarding');
        } catch {
          // History API unavailable — best-effort; URL just stays as-is.
        }
      })
      .catch((err) => {
        // Gate 3 short-circuit: a deterministic UNSUPPORTED_ACCOUNT 403 is NOT
        // the stale-session race. Don't retry through provider_tokens — clear
        // the flag and surface the friendly inline notice instead.
        if (err instanceof UnsupportedAccountError) {
          dispatchedRef.current = false;
          try {
            sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
          } catch {
            // Ignore — clearing is best-effort.
          }
          if (mountedRef.current)
            setUnsupported({ reason: err.reason, email: err.email });
          return;
        }
        // Not a member / not the primary: as deterministic as Gate 3, so say
        // so now instead of silently waiting for a token that cannot help.
        if (isDeterministicSetupRefusal(err)) {
          dispatchedRef.current = false;
          try {
            sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
          } catch {
            // Ignore — clearing is best-effort.
          }
          toast.error(connectFailureToast(err));
          return;
        }
        dispatchedRef.current = false;
        if (hasShownErrorToastRef.current) {
          // Two different tokens have now failed in a row — this isn't the
          // stale-session race, something is actually broken. Stop retrying
          // and tell the user.
          toast.error(connectFailureToast(err));
          try {
            sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
          } catch {
            // Ignore.
          }
        } else {
          // First failure — almost certainly the stale session race.
          // Stay silent; the fresh provider_token Supabase is about to
          // emit (or has already emitted while we were in flight) will
          // re-trigger this effect and the second attempt will succeed.
          hasShownErrorToastRef.current = true;
          // Crucial: if Supabase already swapped session.provider_token
          // during the HTTP call we just lost, React did re-render and
          // re-ran this effect — but `dispatchedRef` was still true at
          // that point so it bailed. The deps won't change again on their
          // own, so kick a nonce state update to force one more pass.
          if (mountedRef.current) setRetryNonce((n) => n + 1);
        }
      })
      .finally(() => {
        if (mountedRef.current) setConnecting(false);
      });
  }, [
    householdId,
    gmailStatus?.state,
    session?.provider_token,
    session?.provider_refresh_token,
    refetchGmailStatus,
    retryNonce,
  ]);

  const startGmailOAuth = async () => {
    // Mark this OAuth round-trip as initiated by Settings so the auto-resume
    // effect runs setupGmailForwarding once Google redirects back. Failing
    // to set the flag just falls back to the manual two-click flow; never a
    // hard blocker.
    try {
      sessionStorage.setItem(OAUTH_IN_PROGRESS_KEY, '1');
    } catch {
      // sessionStorage unavailable (private mode / disabled storage) — ignore.
    }
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: REQUIRED_GMAIL_SCOPES.join(' '),
        redirectTo: `${window.location.origin}/settings/forwarding`,
        queryParams: {
          access_type: 'offline',
          // Never `select_account`: the mailbox we connect must be the signed-in
          // account (YAR-325). `login_hint` preselects it; the match is enforced
          // server-side in `gmail-forwarding-setup`, since a hint is only a hint.
          prompt: 'consent',
          ...(session?.user?.email ? { login_hint: session.user.email } : {}),
        },
      },
    });
  };

  const handleConnect = async () => {
    if (!householdId) return;

    // Fresh attempt. The previous blocked state used to be captured here to
    // force Google's account picker on retry; YAR-325 dropped that, since the
    // connected mailbox must be the signed-in account either way.
    setUnsupported(null);
    setConnecting(true);
    let needsFullOAuth = false;
    let softRefreshThrew = false;
    try {
      // Try a server-side soft refresh first — uses the subscription's
      // stored refresh_token to mint a new access token and rearm the
      // Gmail watch, without sending the user through Google consent.
      // Covers the common case where the 7-day watch expired or the user
      // is resuming after a long pause.
      const refreshResult =
        await householdService.refreshGmailSubscription(householdId);
      if (refreshResult.ok) {
        refetchGmailStatus();
        toast.success('Gmail reconnected');
        return;
      }
      // needs_oauth=true means the stored refresh_token is gone or revoked
      // — typical after a manual Disconnect, which calls
      // oauth2.googleapis.com/revoke and clears refresh_token in the DB.
      // Any session.provider_token still cached client-side belongs to that
      // same revoked OAuth chain: reusing it returns 403
      // ACCESS_TOKEN_SCOPE_INSUFFICIENT from gmail.users.getProfile. So
      // when the server says we need OAuth, we don't trust the cached
      // token — we force a fresh consent regardless.
      needsFullOAuth = refreshResult.needsOAuth;
    } catch {
      // Transient network / server failure on the soft refresh — the server
      // never answered, so we can't conclude the token is dead.
      softRefreshThrew = true;
    } finally {
      setConnecting(false);
    }

    const token = session?.provider_token;

    // A transient soft-refresh failure with no cached provider_token must NOT
    // trigger a full Google consent round-trip: the stored refresh_token is
    // almost certainly still valid (the server never answered), so consent
    // would be a needless reconnect — exactly what we're trying to stop. Let
    // the user retry. (When a fresh provider_token IS cached — e.g. just back
    // from OAuth — we still fall through to setup below.)
    if (softRefreshThrew && !token) {
      toast.error('Could not reconnect right now. Please try again.');
      return;
    }

    if (needsFullOAuth || !token) {
      // After Google redirects back, the auto-resume effect above finishes
      // setup with the fresh provider_token — no second click required.
      // `wasBlocked` used to force the account picker so the user could switch
      // to a personal Gmail. YAR-325 removed that escape: the mailbox must be
      // the signed-in account, so switching is exactly what we now reject.
      // Recovery from a blocked account is to sign in as a usable one.
      await startGmailOAuth();
      return;
    }

    setConnecting(true);
    try {
      const result = await setupGmailForwarding(
        householdId,
        token,
        session?.provider_refresh_token,
      );
      // Same trap as the auto-resume effect: a missing label_id means
      // Gmail rejected label creation (typically stale/under-scoped
      // token) — the HTTP call still returns 200 with errors in the
      // body. Don't surface a false "connected" toast.
      if (!result.label_id) {
        console.error(
          '[SettingsForwarding] Gmail setup returned no label_id:',
          result.errors,
        );
        toast.error(CONNECT_FAILED_TOAST);
        return;
      }
      refetchGmailStatus();
      toast.success('Gmail connected');
    } catch (err) {
      // Gate 3: surface the friendly notice for unsupported mailboxes; only
      // genuinely generic failures fall through to the toast.
      if (err instanceof UnsupportedAccountError) {
        setUnsupported({ reason: err.reason, email: err.email });
        return;
      }
      toast.error(connectFailureToast(err));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!householdId) return;
    setShowDisconnectModal(false);
    // Clear any lingering OAuth flag — if a prior consent didn't complete
    // setup, we don't want the auto-resume effect firing with whatever
    // provider_token is still in session after we just revoked it server-side.
    try {
      sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
    } catch {
      // Storage unavailable — best-effort.
    }
    try {
      // Real disconnect: stops the Gmail watch, revokes the refresh
      // token at Google, and clears refresh_token + watch_expiry on our
      // side. Inbox address and approved senders are preserved so the
      // user can reconnect later via OAuth and resume processing.
      await householdService.disconnectGmailSubscription(householdId);
      refetchGmailStatus();
      toast.success('Gmail disconnected');
    } catch {
      toast.error('Failed to disconnect Gmail');
    }
  };

  const handleToggle = async () => {
    if (!householdId || toggling) return;
    setToggling(true);
    try {
      await applyToggle(householdId);
    } finally {
      setToggling(false);
    }
  };

  const applyToggle = async (householdId: string) => {
    if (state === 'connected') {
      try {
        await householdService.updateGmailSubscriptionStatus(
          householdId,
          false,
        );
        // Awaited: the switch unlocks only once `state` shows the new
        // value, or a tap in that gap would pause a second time.
        await useGmailConnectionStore.getState().refetch();
        toast.success('Forwarding paused');
      } catch {
        toast.error('Failed to pause forwarding');
      }
    } else if (state === 'paused') {
      try {
        await householdService.updateGmailSubscriptionStatus(householdId, true);
        await useGmailConnectionStore.getState().refetch();
        toast.success('Forwarding resumed');
      } catch {
        toast.error('Failed to resume forwarding');
      }
    }
  };

  const showToggle =
    state === 'connected' ||
    state === 'paused' ||
    state === 'reconnect' ||
    state === 'internal_issue';

  const badge = badgeConfig[state];

  const footer = (() => {
    if (loading) return undefined;

    if (state === 'not_connected') {
      return (
        <Button
          variant="primary"
          size="lg"
          className="h-11"
          fullWidth
          onClick={handleConnect}
          loading={connecting}
          disabled={connecting}
        >
          Connect with Gmail
        </Button>
      );
    }

    if (state === 'reconnect' || state === 'paused') {
      // Both states surface the same footer pair: a primary Reconnect CTA
      // (re-runs OAuth + setupGmailForwarding, restoring active=true) and
      // the secondary Disconnect. For `paused` the user can also resume
      // via the toggle above, but Reconnect is the visible escape hatch
      // when they hit Disconnect by mistake or after a token revoke.
      return (
        <div className="flex flex-col gap-3 w-full">
          <Button
            variant="primary"
            size="lg"
            className="h-11"
            fullWidth
            onClick={handleConnect}
            loading={connecting}
            disabled={connecting}
          >
            Reconnect Gmail
          </Button>
          <DisconnectButton onClick={() => setShowDisconnectModal(true)} />
        </div>
      );
    }

    // connected, internal_issue
    return <DisconnectButton onClick={() => setShowDisconnectModal(true)} />;
  })();

  // Defense-in-depth: SettingsMain hides the nav link for non-PRIMARY
  // members, but the route is still reachable by URL (and by banner
  // deep-links in config/banners.ts). Mirror the SettingsPlan guard so
  // only the PRIMARY can ever drive setup/disconnect for the household —
  // caregivers landing here would otherwise mutate the primary's Gmail
  // subscription state.
  if (roleResolved && !isPrimary) {
    return (
      <Navigate
        to="/settings"
        replace
      />
    );
  }

  return (
    <>
      <SettingsSubPage
        title="Email Monitoring"
        // Names its destination instead of letting SettingsSubPage fall back
        // to `navigate(-1)` (YAR-422). Connecting Gmail leaves Google's own
        // page in history, so replaying history after a cancelled sign-in
        // walked the user straight back into the screen they just dismissed.
        // This page is only ever reached from Settings.
        onBack={() => navigate('/settings')}
        stickyFooter={footer}
      >
        <SystemBannerSlot
          scope="page"
          pageContext="forwarding"
          householdId={householdId}
        />
        {loading ? (
          <SettingsForwardingSkeleton />
        ) : (
          <div className="ph-no-capture flex flex-col gap-10 px-5 py-5">
            {/* Status badge — centered */}
            <div className="flex justify-center">
              <span
                className={[
                  'inline-flex items-center gap-1 border rounded-full px-2 py-1',
                  badge.borderColor,
                  badge.bgColor,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span
                  className={['w-2.5 h-2.5 rounded-full', badge.dotColor].join(
                    ' ',
                  )}
                />
                <span className={['text-sm', badge.textColor].join(' ')}>
                  {badge.label}
                </span>
              </span>
            </div>

            {/* Gate 3: unsupported mailbox (Workspace/business or missing
                scopes). Surfaced after a reconnect/connect attempt fails. */}
            {unsupported && (
              <UnsupportedAccountNotice
                reason={unsupported.reason}
                email={unsupported.email}
              />
            )}

            {/* Not connected */}
            {state === 'not_connected' && (
              <p className="text-sm text-muted-foreground">
                Connect your Gmail account to let Yaruta automatically read and
                process school emails. No emails are stored — only the extracted
                events and tasks are saved.
              </p>
            )}

            {/* Connected / Paused — toggle card */}
            {showToggle && (
              <div className="border border-border rounded-lg">
                <div className="flex gap-2 items-center px-4 py-3">
                  <div className="flex flex-col gap-0.5 flex-1">
                    <span className="text-base font-medium text-foreground">
                      Allow Yaruta to read emails
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Turning this off pauses email reading without removing
                      your Gmail connection.
                    </span>
                  </div>
                  <Switch
                    checked={state === 'connected' || state === 'reconnect'}
                    onChange={handleToggle}
                    disabled={
                      toggling ||
                      state === 'reconnect' ||
                      state === 'internal_issue'
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </SettingsSubPage>

      {/* Disconnect confirmation modal */}
      <Modal
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          <p className="text-xl font-medium text-foreground text-center w-full">
            Disconnect Gmail?
          </p>
          <p className="text-sm text-muted-foreground text-center leading-5">
            We'll stop the Gmail watch and revoke access to your account. Your
            inbox address and approved senders stay saved — reconnect anytime to
            resume processing.
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button
              type="button"
              onClick={handleDisconnect}
              className="w-full border border-danger text-danger font-semibold text-base leading-6 py-1.5 px-2 rounded-md cursor-pointer"
            >
              Disconnect
            </button>
            <button
              type="button"
              onClick={() => setShowDisconnectModal(false)}
              className="w-full text-muted-foreground font-semibold text-base leading-6 py-1.5 px-2 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
